import React, { useState, useEffect, useReducer, useRef } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Card,
  TableHead,
  TablePagination,
  Skeleton,
  CardContent,
  Container,
  Tooltip,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Input,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import NoData from "components/NoData";
import ContactNote from "./ContactNote";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Link, useParams } from "react-router-dom";
import CropFreeIcon from "@mui/icons-material/CropFree";
import CloseIcon from "@mui/icons-material/Close";
import SplitterLayout from "react-splitter-layout";
import "react-splitter-layout/lib/index.css";
import TemplateBox from "components/TemplateBox";
import { RenderDeviceNote } from "./renderDeviceNoteTables";
import dayjs from "dayjs";
import Contacts from "../Contacts";
import StoreNetworkDialog from "popup/StoreNetworkDialog";
import { I18n } from "aws-amplify/utils";
import StoreCard from "./StoreCard";
import ContactsCard from "./ContactsCard";
import DetailsCard from "./DetailsCard";
import NotesCard from "./NotesCard";
import { RenderPlantNote } from "./renderPlantNoteTable";
import { tlmGetAccountAssets } from "layouts/dashboard/apiService";
import WarningIcon from "@mui/icons-material/Warning";
import { tlmPostAccountNumberActive } from "layouts/dashboard/apiService";
import { tlmPostAccountNumberInactive } from "layouts/dashboard/apiService";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { grey } from "@mui/material/colors";
import { useTemplateController } from "context";
import { useNavigate } from "react-router-dom";
import { tlmGetPlantNotes } from "layouts/dashboard/apiService";
import LaunchIcon from "@mui/icons-material/Launch";
import { fetchAlarmsOnSameDevice } from "layouts/dashboard/apiService";
import {
  fetchResourcePref,
  fetchAccNumberContacts,
} from "layouts/dashboard/apiService";
import { tlmGetDeviceNote } from "layouts/dashboard/apiService";
import { RenderProcessNote } from "./renderProcessNoteTables";
import { tlmGetProcessNotes } from "layouts/dashboard/apiService";
import InactivateStoreDialog from "popup/InactivateStoreDialog";
import { APIStatusDialog } from "components/APIStatusDialog";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import {
  AlarmOffOutlined,
  PauseCircleFilledOutlined,
} from "@mui/icons-material";
import ActivateStoreDialog from "popup/ActivateStoreDialog";
import { format } from "date-fns";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, DateTimeField } from "@mui/x-date-pickers";
import SearchIcon from '@mui/icons-material/Search';
import AlarmsOnSameStorePopup from "popup/AlarmsOnSameStorePopup";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const initialState = {
  loading: true,
  data: null,
  error: null,
};

const formatDateTime = (datetime) => {
  try {
    return dayjs.utc(datetime).tz(dayjs.tz.guess()).format("YYYY-MM-DD HH:mm:ss");
  } catch (error) {
    console.error("Invalid date format:", datetime);
    return datetime; // Ritorna il valore originale se non è una data valida
  }
};
function processDeviceReducer(state, action) {
  switch (action.type) {
    case "DATA_RECEIVED":
      return { ...state, loading: false, data: action.value, error: null };
    case "ERROR":
      return { ...state, loading: false, error: action.error };
    default:
      return state;
  }
}
function noteDeviceReducer(state, action) {
  switch (action.type) {
    case "DATA_RECEIVED":
      return { ...state, loading: false, data: action.value, error: null };
    case "ERROR":
      return { ...state, loading: false, error: action.error };
    default:
      return state;
  }
}
function notePlantReducer(state, action) {
  switch (action.type) {
    case "DATA_RECEIVED":
      return { ...state, loading: false, data: action.value, error: null };
    case "ERROR":
      return { ...state, loading: false, error: action.error };
    default:
      return state;
  }
}
function assetReducer(state, action) {
  switch (action.type) {
    case "DATA_RECEIVED":
      return { ...state, loading: false, data: action.value, error: null };
    case "ERROR":
      return { ...state, loading: false, error: action.error };
    default:
      return state;
  }
}
function alarmsSameDevReducer(state, action) {
  switch (action.type) {
    case "DATA_RECEIVED":
      return { ...state, loading: false, data: action.value, error: null };
    case "ERROR":
      return { ...state, loading: false, error: action.error };
    default:
      return state;
  }
}
function resourcePrefReducer(state, action) {
  switch (action.type) {
    case "DATA_RECEIVED":
      return { ...state, loading: false, data: action.value, error: null };
    case "ERROR":
      return { ...state, loading: false, error: action.error };
    default:
      return state;
  }
}
function contactAccNumberReducer(state, action) {
  switch (action.type) {
    case "DATA_RECEIVED":
      return { ...state, loading: false, data: action.value, error: null };
    case "ERROR":
      return { ...state, loading: false, error: action.error };
    default:
      return state;
  }
}

const ActionComponent = ({ selectedRow }) => {
  const navigate = useNavigate();
  return (
    <Tooltip title="View Details">
      <IconButton
        onClick={() => navigate(`/dashboard/alarm-details/${selectedRow.id}`)}
      >
        <LaunchIcon />
      </IconButton>
    </Tooltip>
  );
};
const StateComponent = ({ value }) => {
  const [controller] = useTemplateController();
  const { darkMode } = controller;

  const stateColor = darkMode ? grey[200] : grey[800];

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {value === "0" ? (
        <Stack direction="row">
          {"Inactive"}{" "}
          <CheckCircleIcon
            sx={{ m: 1, width: 20, height: 20, color: stateColor }}
          />
        </Stack>
      ) : value === "1" ? (
        <Stack direction="row">
          {"Active"}{" "}
          <WarningIcon
            sx={{ m: 1, width: 20, height: 20, color: stateColor }}
          />
        </Stack>
      ) : value === "2" ? (
        <Stack direction="row">
          {"Close"}{" "}
          <AlarmOffOutlined
            sx={{ m: 1, width: 20, height: 20, color: stateColor }}
          />
        </Stack>
      ) : value === "3" ? (
        <Stack direction="row">
          {"Suspended"}{" "}
          <PauseCircleFilledOutlined
            sx={{ m: 1, width: 20, height: 20, color: stateColor }}
          />
        </Stack>
      ) : value === "4" ? (
        <Stack direction="row">
          {"Suspended With Lock"}{" "}
          <PauseCircleOutlineIcon
            sx={{ m: 1, width: 20, height: 20, color: stateColor }}
          />
        </Stack>
      ) : (
        <Stack direction="row">
          {"Unknown"}{" "}
          <WarningIcon
            sx={{ m: 1, width: 20, height: 20, color: stateColor }}
          />
        </Stack>
      )}
    </Box>
  );
};
const SeverityComponent = ({ alarm_type_description, color }) => {
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Typography>{alarm_type_description}</Typography>
      <Box
        sx={{
          width: 25,
          height: 20,
          borderRadius: "50%",
          backgroundColor: color, // Usa il colore dell'allarme o un valore di fallback
        }}
      />
    </Stack>
  );
};
function StoreDetails({
  isOpen,
  onClose,
  rowData,
  onCloseNote,
  loading,
  language,
  profile,
  tlmGetAccoutsCountryCode,
}) {
  const data = rowData && rowData.length > 0 ? rowData[0] : {};
  const [apiStatus, setApiStatus] = useState({
    open: false,
    status: "",
    message: "",
  });
  const [endDate, setEndDate] = useState(dayjs().endOf("day"));
  const [startDate, setStartDate] = useState(
    dayjs(endDate).subtract(48, "hour")
  );
  const [endDateDev, setEndDateDev] = useState(dayjs().endOf("day"));
  const [startDateDev, setStartDateDev] = useState(
    dayjs(endDate).subtract(48, "hour")
  );
  const [notePlantState, dispatchNotePlant] = useReducer(
    notePlantReducer,
    initialState
  );
  const [devicePlantState, dispatchDevicePlant] = useReducer(
    noteDeviceReducer,
    initialState
  );
  const [deviceProscessState, dispatchProcessPlant] = useReducer(
    processDeviceReducer,
    initialState
  );
  const [isStoreNetworkeDialogOpen, setIsStoreNetworkDialogOpen] =
    useState(false);
  const [isInactiveStoreDialogOpen, setIsInactiveStoreDialogOpen] =
    useState(false);
  const toolbarRef = useRef(null); // Reference for the toolbar
  const [tableParentIsMD, setTableParentIsMD] = useState(false); // Manage toolbar responsiveness
  const [resourcePrefPage, setResourcePrefPage] = useState(0);
  const [rowsPerResourcePrefPage, setRowsPerResourcePrefPage] = useState(5);
  const [resourcePrefPageDev, setResourcePrefPageDev] = useState(0);
  const [rowsPerResourcePrefPageDev, setRowsPerResourcePrefPageDev] =
    useState(5);
  const [resourcePrefPagePro, setResourcePrefPagePro] = useState(0);
  const [rowsPerResourcePrefPagePro, setRowsPerResourcePrefPagePro] =
    useState(5);
  const [assetState, dispatchAssetState] = useReducer(
    assetReducer,
    initialState
  );
  const [isActivateStoreDialogOpen, setIsActivateStoreDialogOpen] =
    useState(false);
  const [storeStatus, setStoreStatus] = useState(
    data.inactive_store ? "inactive" : "active"
  ); // Stato del negozio
  const durationOptions = [
    { value: 1, label: I18n.get("1 hour") },
    { value: 3, label: I18n.get("3 hours") },
    { value: 6, label: I18n.get("6 hours") },
    { value: 12, label: I18n.get("12 hours") },
    { value: 24, label: I18n.get("24 hours") },
    { value: 168, label: I18n.get("Last 7 days") },
    { value: 504, label: I18n.get("Last 21 days") },
    { value: 672, label: I18n.get("Last 28 days") },
    { value: "custom", label: I18n.get("Selected Date") },
  ];
  const [startDateCustom, setStartDateCustom] = useState(dayjs().tz().format("YYYY-MM-DD 00:00:00"));
  const [endDateCustom, setEndDateCustom] = useState(dayjs().tz().format("YYYY-MM-DD 01:00:00"));
  const [showCustomDate, setShowCustomDate] = useState(false);
  const [searchCustom, setSearchCustom] = useState(false);
  const [searchText, setSearchText] = useState("");
  const searchChanged = (event) => {
    setSearchText(event.target.value);
  };
  const [filteredRows, setFilteredRows] = useState([]);
  const [isAlarmDialogOpen, setIsAlarmDialogOpen] = useState(false);
  
  const [alarmsSameDevState, dispatchAlarmsSameDev] = useReducer(
    alarmsSameDevReducer,
    initialState
  );
  const [resourcePrefState, dispatchResourcePref] = useReducer(
    resourcePrefReducer,
    initialState
  );
  const [contactAccNumberState, dispatchContactAccNumber] = useReducer(
    contactAccNumberReducer,
    initialState
  );
  const [duration, setDuration] = useState(24); // Default to 24 hours

  const [controller] = useTemplateController();
  const { darkMode } = controller;
  const handleDurationChange = (selectedValue) => {
    setShowCustomDate(selectedValue === "custom");

    if (selectedValue === "custom") {
      setDuration(selectedValue);
    } else {
      setDuration(selectedValue);
    // Calcola startDate e endDate
      const now = new Date();
      let startDate;
      if (selectedValue === 168) {
        // Per gli ultimi 7 giorni
        startDate = new Date();
        startDate.setDate(now.getDate() - 7); // Sottrai 7 giorni
      } else if (selectedValue === 504) {
        startDate = new Date();
        startDate.setDate(now.getDate() - 21);
      } else if (selectedValue === 672) {
        startDate = new Date();
        startDate.setDate(now.getDate() - 28);
      } else {
        // Per le ore
        startDate = new Date();
        startDate.setHours(now.getHours() - selectedValue); // Sottrai ore
      }
      // Format Date
      const formattedStartDate = startDate.toISOString(); // Puoi formattare diversamente se necessario
      const formattedEndDate = now.toISOString(); // Fine periodo è sempre ora corrente
    }
  };

  const [alarmsSameDevPage, setAlarmsSameDevPage] = useState(0);
  const [rowsPerAlarmsSameDevPage, setRowsPerAlarmsSameDevPage] = useState(5);
  const isGuest = profile.includes("Guest") || profile.includes("Operator"); // Check if profile includes "Guest"

  useEffect(() => {
    if (searchText) {
      const filtered = alarmsSameDevState.data.filter((row) => {
        return Object.values(row).some((val) =>
          String(val).toLowerCase().includes(searchText.toLowerCase())
        );
      });
      setFilteredRows(filtered);
    } else {
      setFilteredRows(alarmsSameDevState.data);
    }
  },[alarmsSameDevState.data, searchText]);

  useEffect(() => {
    setSearchText("");
    setDuration(24); // Reset duration to 24 hours
    setShowCustomDate(false);
  },[data.account_number]);

  useEffect(() => {
    const parentElement = toolbarRef.current;
    if (!parentElement) {
      console.error("toolbarRef is not correctly attached to any DOM element.");
      return;
    }
    const checkSize = (width) => {
      const breakpoint = 960; // 960px is md
      setTableParentIsMD(width < breakpoint);
    };
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        checkSize(entry.contentRect.width);
      }
    });
    resizeObserver.observe(parentElement);
    checkSize(parentElement.offsetWidth);
    return () => {
      resizeObserver.disconnect();
    };
  }, []);
  useEffect(() => {
    if (!loading && rowData && data.account_number) {
      const fetchData = async () => {
        try {
          const response = await tlmGetPlantNotes(
            data.account_number,
            endDate,
            startDate
          );
          dispatchNotePlant({ type: "DATA_RECEIVED", value: response });
        } catch (error) {
          dispatchNotePlant({ type: "ERROR", error });
        }
      };
      fetchData();
    }
  }, [loading, data, language, endDate, startDate]);
  useEffect(() => {
    if (!loading && rowData && data.account_number) {
      const fetchData = async () => {
        try {
          const response = await tlmGetDeviceNote(
            data.account_number,
            endDateDev,
            startDateDev,
            language
          );
          dispatchDevicePlant({ type: "DATA_RECEIVED", value: response });
        } catch (error) {
          dispatchDevicePlant({ type: "ERROR", error });
        }
      };
      fetchData();
    }
  }, [loading, data, language, endDateDev, startDateDev]);
  useEffect(() => {
    if (!loading && rowData && data.account_number) {
      const fetchData = async () => {
        try {
          const response = await tlmGetProcessNotes(
            data.account_number,
            endDateDev,
            startDateDev,
            language
          );
          dispatchProcessPlant({ type: "DATA_RECEIVED", value: response });
        } catch (error) {
          dispatchProcessPlant({ type: "ERROR", error });
        }
      };
      fetchData();
    }
  }, [loading, data, endDateDev, startDateDev, language]);

  useEffect(() => {
    setShowCustomDate(false);
    setDuration(24); // Reset duration to 24 hours
    setStartDateCustom(dayjs().tz().format("YYYY-MM-DD 00:00:00"));
    setEndDateCustom(dayjs().tz().format("YYYY-MM-DD 01:00:00"));
  }, []);

  const alarmColumns = [
    {
      id: "name",
      label: I18n.get("Name"),
      align: "left",
      flex: 1,
      rendercell: (params) => (
        <Typography
          component="a"
          href={`https://eptarefrigeration.lightning.force.com/${params.row.contact_id}`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            fontSize: "medium",
            cursor: "pointer",
            textDecoration: "underline",
          }}
        >
          {params.value}
        </Typography>
      ),
    },

    {
      id: "phone",
      label: I18n.get("Phone"),
      align: "left",
      flex: 1,
      rendercell: (params) => (
        <Typography
          component="a"
          href={`tel:${params.value}`}
          sx={{
            fontSize: "medium",
            cursor: "pointer",
            textDecoration: "underline",
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      id: "mobile_phone",
      label: I18n.get("Mobile"),
      align: "left",
      flex: 1,
      rendercell: (params) => (
        <Typography
          component="a"
          href={`tel:${params.value}`}
          sx={{
            fontSize: "medium",
            cursor: "pointer",
            textDecoration: "underline",
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      id: "email",
      label: I18n.get("Email"),
      align: "left",
      flex: 1,
      rendercell: (params) => (
        <Typography
          component="a"
          href={`mailto:${params.value}`}
          sx={{
            fontSize: "medium",
            cursor: "pointer",
            textDecoration: "underline",
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      id: "fsl_priority",
      label: I18n.get("Priority"),
      align: "left",
      flex: 1,
    },
    {
      id: "fsl_contact_availability",
      label: I18n.get("Contact Availability"),
      align: "left",
      flex: 1,
    },
    {
      id: "fsl_contact_availability_notes",
      label: I18n.get("Contact Availability Notes"),
      align: "left",
      flex: 1,
    },
  ];
  const assetColumns = [
    {
      id: "fsl_name_and_serial_number",
      label: I18n.get("Name"),
      align: "left",
      flex: 1,
      rendercell: (params) => {
        return (
          <Typography
            component="a"
            href={`https://eptarefrigeration.lightning.force.com/${params.row.id}`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              fontSize: "medium",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            {params.value}
          </Typography>
        );
      },
    },
    {
      id: "system_technology",
      label: I18n.get("Technology"),
      align: "left",
      flex: 1,
    },
    {
      id: "type_of_ip_connect",
      label: I18n.get("Type of IP Connect"),
      align: "left",
      flex: 1,
    },
    {
      id: "number_of_monitored_devices",
      label: I18n.get("N. Devices"),
      align: "left",
      flex: 1,
    },
    {
      id: "fsl_url_link",
      label: I18n.get("Direct Link"),
      align: "left",
      flex: 1,
      rendercell: (params) => {
        return (
          <Typography
            component="a"
            href={params.value}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              fontSize: "medium",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            {I18n.get("Open link")}
          </Typography>
        );
      },
    },
  ];

  const resourcePrefColumns = [
    {
      id: "fsl_service_resource_account",
      label: I18n.get("Service Resource Account"),
      align: "left",
      flex: 1,
      rendercell: (params) => {
        return (
          <Typography
            component="a"
            href={`https://eptarefrigeration.lightning.force.com/${params.row.fsl_partner_account}`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              fontSize: "medium",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            {params.value}
          </Typography>
        );
      },
    },
    {
      id: "resource_preference_number",
      label: I18n.get("Resource Preference Number"),
      align: "left",
      flex: 1,
      rendercell: (params) => {
        return (
          <Typography
            component="a"
            href={`https://eptarefrigeration.lightning.force.com/${params.row.id}`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              fontSize: "medium",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            {params.value}
          </Typography>
        );
      },
    },
    {
      id: "fsl_work_type_category",
      label: I18n.get("Work Type Category"),
      align: "left",
      flex: 1,
    },
  ];
  const storesColumns = [
    {
      id: "datetime_start",
      label: I18n.get("Start Date"),
      align: "left",
      flex: 1,
      rendercell: (params) => {
        if (params.rowNode?.type === "group") {
          return undefined;
        }
        return formatDateTime(params.value);
      },
    },
    {
      id: "alarm_note",
      label: I18n.get("Remark"),
      align: "left",
      flex: 1,
    },
    {
      id: "process_note",
      label: I18n.get("Process Note"),
      align: "left",
      flex: 1,
    },
    {
      id: "original_equipment",
      label: I18n.get("Original Equipment"),
      align: "left",
      flex: 1,
    },
    {
      id: "position",
      label: I18n.get("Position"),
      align: "left",
      flex: 1,
    },
    {
      id: "alarm_text",
      label: I18n.get("Alarm Text"),
      align: "left",
      flex: 1,
    },
    {
      id: "alarm_type_description",
      label: I18n.get("Alarm Type Description"),
      align: "left",
      flex: 1,
      rendercell: (params) => {
        if (params.rowNode?.type === "group") {
          return undefined;
        }
        const { alarm_type_description, color } = params.row;
        return (
          <SeverityComponent
            alarm_type_description={alarm_type_description}
            color={color}
          />
        );
      },
    },
    {
      id: "state",
      label: I18n.get("State"),
      align: "left",
      flex: 1,
      rendercell: (params) => {
        if (params.rowNode?.type === "group") {
          return undefined;
        }
        return <StateComponent value={params.value} />;
      },
    },
  ];

  useEffect(() => {
    if (!loading && data && data.account_number) {
      if(duration === "custom" && searchCustom) {
        const fetchData = async () => {
          dispatchAlarmsSameDev({ type: "LOADING" });
          try {
            const startDateTimeUTC = dayjs(startDateCustom).utc().format();
            const endDateTimeUTC = dayjs(endDateCustom).utc().format();
            const response = await fetchAlarmsOnSameDevice(
              data.account_number,
              language,
              startDateTimeUTC,
              endDateTimeUTC
            );
            dispatchAlarmsSameDev({ type: "DATA_RECEIVED", value: response });
          } catch (error) {
            dispatchAlarmsSameDev({ type: "ERROR", error });
          }
        };
        fetchData();
        setSearchCustom(false);
      } else if (duration !== "custom") {
        const fetchData = async () => {
          dispatchAlarmsSameDev({ type: "LOADING" });
          try {
            const now = dayjs().utc();
            const startDateTimeUTC = now.subtract(duration, "hour").format();
            const endDateTimeUTC = now.format();
            const response = await fetchAlarmsOnSameDevice(
              data.account_number,
              language,
              startDateTimeUTC,
              endDateTimeUTC
            );
            dispatchAlarmsSameDev({ type: "DATA_RECEIVED", value: response });
          } catch (error) {
            dispatchAlarmsSameDev({ type: "ERROR", error });
          }
        };
        fetchData();
      }
    }
  }, [loading, data, language, duration, searchCustom]);

  useEffect(() => {
    if (!loading && rowData && data.account_number) {
      const fetchData = async () => {
        try {
          const response = await tlmGetAccountAssets(data.id);
          dispatchAssetState({ type: "DATA_RECEIVED", value: response });
        } catch (error) {
          dispatchAssetState({ type: "ERROR", error });
        }
      };
      fetchData();
    }
  }, [loading, data, language]);
  useEffect(() => {
    if (!loading && rowData && data.account_number) {
      const fetchData = async () => {
        try {
          const response = await fetchResourcePref(data.id);
          dispatchResourcePref({ type: "DATA_RECEIVED", value: response });
        } catch (error) {
          dispatchResourcePref({ type: "ERROR", error });
        }
      };
      fetchData();
    }
  }, [loading, data]);
  useEffect(() => {
    if (!loading && rowData && data.account_number) {
      const fetchData = async () => {
        try {
          const response = await fetchAccNumberContacts(data.id);
          dispatchContactAccNumber({ type: "DATA_RECEIVED", value: response });
        } catch (error) {
          dispatchContactAccNumber({ type: "ERROR", error });
        }
      };
      fetchData();
    }
  }, [loading, data]);

  function renderAlarmsOnSameDevice() {
    if (alarmsSameDevState.loading) {
      return (
        <Card>
          <CardContent>
            <Skeleton variant="rectangular" width="100%" height={400} />
          </CardContent>
        </Card>
      );
    }

    if (!filteredRows|| filteredRows.length === 0) {
      // if (!alarmsSameDevState.data || alarmsSameDevState.data.length === 0) {
      return (
        <Card>
          <CardContent
            sx={{ padding: "1rem", "&:last-child": { padding: "1rem" } }}
          >
            <Stack
              spacing={0}
              direction="row"
              alignItems="center"
              justifyContent="flex-end"
              sx={{ pb: 2 }}
            >
              <Typography variant="h5">
                {I18n.get("Alarms on Same Store - ") +
                  (duration === 168 ? I18n.get("Last 7 Days") : duration === "custom" ? I18n.get("Selected date") : duration === 504 ? I18n.get("Last 21 Days") : duration === 672 ? I18n.get("Last 28 Days") : `Last ${duration}H`)}
              </Typography>
              <IconButton
                onClick={() => {setIsAlarmDialogOpen(true);}}
                size="medium"
                color="primary"
              >
                <OpenInNewIcon />
              </IconButton>
            </Stack>
            <Stack
              direction={tableParentIsMD ? "column" : "row"}
              alignItems="center"
              justifyContent={tableParentIsMD ? "center" : "space-between"}
              sx={{ pb: 1 }}
            >
            <Box sx={{ width: "200px" }}>
              <FormControl margin="dense" fullWidth>
                <InputLabel>{I18n.get("Duration")}</InputLabel>
                <Select
                  label="Duration"
                  value={duration}
                  onChange={(e) => handleDurationChange(e.target.value)}
                >
                  {durationOptions.map(({ value, label }) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ width: "200px" }}>
              <Input
                type="text"
                placeholder={I18n.get("Search")}
                value={searchText}
                onChange={searchChanged}
                fullWidth={tableParentIsMD}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton>
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                }
              />
              </Box>
            </Stack>
            { showCustomDate && (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box
                  display="flex"
                  alignItems="center"
                  width="100%"
                  gap={1}
                  marginTop={2}
                >
                  <DateTimeField
                    label={I18n.get("Start Date")}
                    format="YYYY/MM/DD HH:mm"
                    value={dayjs(startDateCustom)}
                    onChange={(newDate) => {
                      const formattedDate = dayjs(newDate).format("YYYY-MM-DD HH:mm:ss");
                      setStartDateCustom(formattedDate);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        fullWidth
                        variant="outlined"
                        InputProps={{
                          style: {
                            padding: "4px",
                            fontSize: "14px",
                          },
                        }}
                      />
                    )}
                  />
                  <DateTimeField
                    label={I18n.get("End Date")}
                    format="YYYY/MM/DD HH:mm"
                    value={dayjs(endDateCustom)}
                    onChange={(newDate) => {
                      const formattedDate = dayjs(newDate).format("YYYY-MM-DD HH:mm:ss");
                      setEndDateCustom(formattedDate);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        fullWidth
                        variant="outlined"
                        InputProps={{
                          style: {
                            padding: "4px",
                            fontSize: "14px",
                          },
                        }}
                      />
                    )}
                  />
                <IconButton
                  onClick={() => {handleDurationChange("custom"); setSearchCustom(true);}}
                  size="large"
                  color="primary"
                >
                  <SearchIcon />
                </IconButton>
                </Box>
              </LocalizationProvider>
            )}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <NoData />
            </Box>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card>
        <CardContent
          sx={{ padding: "1rem", "&:last-child": { padding: "1rem" } }}
        >
          <Stack
            spacing={1}
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            sx={{ pb: 2 }}
          >
            <Typography variant="h5">
              {I18n.get("Alarms on Same Store - ") +
                (duration === 168 ? I18n.get("Last 7 Days") : duration === "custom" ? I18n.get("Selected date") : duration === 504 ? I18n.get("Last 21 Days") : duration === 672 ? I18n.get("Last 28 Days") :`Last ${duration}H`)}
          </Typography>
          <IconButton
            onClick={() => {setIsAlarmDialogOpen(true);}}
            size="medium"
            color="primary"
          >
            <OpenInNewIcon />
          </IconButton>
          </Stack>
          <Stack
            direction={tableParentIsMD ? "column" : "row"}
            alignItems="center"
            justifyContent={tableParentIsMD ? "center" : "space-between"}
            sx={{ pb: 1 }}
          >
          <Box sx={{ width: "200px" }}>
            <FormControl margin="dense" fullWidth>
              <InputLabel>{I18n.get("Duration")}</InputLabel>
              <Select
                label="Duration"
                value={duration}
                onChange={(e) => handleDurationChange(e.target.value)}
              >
                {durationOptions.map(({ value, label }) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ width: "200px" }}>
            <Input
              type="text"
              placeholder={I18n.get("Search")}
              onChange={searchChanged}
              fullWidth={tableParentIsMD}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              }
            />
          </Box>
          </Stack>
          { showCustomDate && (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box
                  display="flex"
                  alignItems="center"
                  width="100%"
                  gap={1}
                  marginTop={2}
                >
                  <DateTimeField
                    label={I18n.get("Start Date")}
                    format="YYYY/MM/DD HH:mm"
                    value={dayjs(startDateCustom)}
                    onChange={(newDate) => {
                      const formattedDate = dayjs(newDate).format("YYYY-MM-DD HH:mm:ss");
                      setStartDateCustom(formattedDate);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        fullWidth
                        variant="outlined"
                        InputProps={{
                          style: {
                            padding: "4px",
                            fontSize: "14px",
                          },
                        }}
                      />
                    )}
                  />
                  <DateTimeField
                    label={I18n.get("End Date")}
                    format="YYYY/MM/DD HH:mm"
                    value={dayjs(endDateCustom)}
                    onChange={(newDate) => {
                      const formattedDate = dayjs(newDate).format("YYYY-MM-DD HH:mm:ss");
                      setEndDateCustom(formattedDate);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        fullWidth
                        variant="outlined"
                        InputProps={{
                          style: {
                            padding: "4px",
                            fontSize: "14px",
                          },
                        }}
                      />
                    )}
                  />
                <IconButton
                  onClick={() => { setSearchCustom(true); handleDurationChange("custom");}}
                  size="large"
                  color="primary"
                >
                  <SearchIcon />
                </IconButton>
                </Box>
              </LocalizationProvider>
            )}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {storesColumns.map((column) => (
                    <TableCell key={column.id}>{column.label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {//alarmsSameDevState.data
                  filteredRows
                  .slice(
                    alarmsSameDevPage * rowsPerAlarmsSameDevPage,
                    alarmsSameDevPage * rowsPerAlarmsSameDevPage +
                      rowsPerAlarmsSameDevPage
                  )
                  .map((mappingRow, rowIndex) => (
                    <TableRow key={rowIndex}>
                      {storesColumns.map((column, colIndex) => (
                        <TableCell key={colIndex}>
                          {column.rendercell
                            ? column.rendercell({
                                value: mappingRow[column.id],
                                row: mappingRow,
                              })
                            : mappingRow[column.id] !== undefined &&
                              mappingRow[column.id] !== null
                            ? mappingRow[column.id]
                            : ""}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={alarmsSameDevState.data.length}
            page={alarmsSameDevPage}
            onPageChange={(event, newPage) => setAlarmsSameDevPage(newPage)}
            rowsPerPage={rowsPerAlarmsSameDevPage}
            onRowsPerPageChange={(event) => {
              setRowsPerAlarmsSameDevPage(parseInt(event.target.value, 10));
              setAlarmsSameDevPage(0);
            }}
            rowsPerPageOptions={[]}
          />
        </CardContent>
      </Card>
    );
  }

  function renderResourcePref() {
    if (resourcePrefState.loading) {
      return (
        <Card>
          <CardContent>
            <Skeleton variant="rectangular" width="100%" height={400} />
          </CardContent>
        </Card>
      );
    }

    if (!resourcePrefState.data || resourcePrefState.data.length === 0) {
      return (
        <Card sx={{ marginBottom: "1rem" }}>
          <CardContent
            sx={{ padding: "1rem", "&:last-child": { padding: "1rem" } }}
          >
            <Stack
              spacing={2}
              direction="row"
              alignItems="center"
              justifyContent="flex-end"
              sx={{ pb: 2 }}
            >
              <Typography variant="h5">
                {I18n.get("Resource Preference")}
              </Typography>
            </Stack>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <NoData />
            </Box>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card sx={{ marginBottom: "1rem" }}>
        <CardContent
          sx={{ padding: "1rem", "&:last-child": { padding: "1rem" } }}
        >
          <Stack
            spacing={2}
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            sx={{ pb: 2 }}
          >
            <Typography variant="h5">
              {I18n.get("Resource Preference")}
            </Typography>
          </Stack>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {resourcePrefColumns.map((column) => (
                    <TableCell key={column.id}>{column.label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {resourcePrefState.data
                  .slice(
                    resourcePrefPage * rowsPerResourcePrefPage,
                    resourcePrefPage * rowsPerResourcePrefPage +
                      rowsPerResourcePrefPage
                  )
                  .map((mappingRow, rowIndex) => (
                    <TableRow key={rowIndex}>
                      {resourcePrefColumns.map((column, colIndex) => (
                        <TableCell key={colIndex} sx={{ padding: "0px" }}>
                          {column.rendercell
                            ? column.rendercell({
                                value: mappingRow[column.id],
                                row: mappingRow,
                              })
                            : mappingRow[column.id] !== undefined &&
                              mappingRow[column.id] !== null
                            ? mappingRow[column.id]
                            : ""}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={resourcePrefState.data.length}
            page={resourcePrefPage}
            onPageChange={(event, newPage) => setResourcePrefPage(newPage)}
            rowsPerPage={rowsPerResourcePrefPage}
            onRowsPerPageChange={(event) => {
              setRowsPerResourcePrefPage(parseInt(event.target.value, 10));
              setResourcePrefPage(0);
            }}
            rowsPerPageOptions={[]}
          />
        </CardContent>
      </Card>
    );
  }
  function renderAsset() {
    if (assetState.loading) {
      return (
        <Card>
          <CardContent>
            <Skeleton variant="rectangular" width="100%" height={400} />
          </CardContent>
        </Card>
      );
    }

    if (!assetState.data || assetState.data.length === 0) {
      return (
        <Card sx={{ marginBottom: "1rem" }}>
          <CardContent sx={{ "&:last-child": { padding: "1rem" } }}>
            <Stack
              spacing={2}
              direction="row"
              alignItems="center"
              justifyContent="flex-end"
              sx={{ pb: 2 }}
            >
              <Typography variant="h5">
                {I18n.get("Telemonitoring Asset")}
              </Typography>
            </Stack>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <NoData />
            </Box>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card sx={{ marginBottom: "1rem" }}>
        <CardContent sx={{ "&:last-child": { padding: "1rem" } }}>
          <Stack
            spacing={2}
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            sx={{ pb: 2 }}
          >
            <Typography variant="h5">
              {I18n.get("Telemonitoring Asset")}
            </Typography>
          </Stack>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {assetColumns.map((column) => (
                    <TableCell key={column.id}>{column.label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {assetState.data
                  .slice(
                    resourcePrefPage * rowsPerResourcePrefPage,
                    resourcePrefPage * rowsPerResourcePrefPage +
                      rowsPerResourcePrefPage
                  )
                  .map((mappingRow, rowIndex) => (
                    <TableRow key={rowIndex}>
                      {assetColumns.map((column, colIndex) => (
                        <TableCell key={colIndex} sx={{ padding: "0px" }}>
                          {column.rendercell
                            ? column.rendercell({
                                value: mappingRow[column.id],
                                row: mappingRow,
                              })
                            : mappingRow[column.id] !== undefined &&
                              mappingRow[column.id] !== null
                            ? mappingRow[column.id]
                            : ""}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={assetState.data.length}
            page={resourcePrefPage}
            onPageChange={(event, newPage) => setResourcePrefPage(newPage)}
            rowsPerPage={rowsPerResourcePrefPage}
            onRowsPerPageChange={(event) => {
              setRowsPerResourcePrefPage(parseInt(event.target.value, 10));
              setResourcePrefPage(0);
            }}
            rowsPerPageOptions={[]}
          />
        </CardContent>
      </Card>
    );
  }
  function renderContactAccInfo() {
    if (contactAccNumberState.loading) {
      return (
        <Card>
          <CardContent>
            <Skeleton variant="rectangular" width="100%" height={400} />
          </CardContent>
        </Card>
      );
    }

    if (
      !contactAccNumberState.data ||
      contactAccNumberState.data.length === 0
    ) {
      return (
        <Card sx={{ marginBottom: "1rem" }}>
          <CardContent sx={{ "&:last-child": { padding: "1rem" } }}>
            <Stack
              spacing={2}
              direction="row"
              alignItems="center"
              justifyContent="flex-end"
              sx={{ pb: 2 }}
            >
              <Typography variant="h5">
                {I18n.get("Telemonitoring Active Contacts")}
              </Typography>
            </Stack>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <NoData />
            </Box>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card sx={{ marginBottom: "1rem" }}>
        <CardContent sx={{ "&:last-child": { padding: "1rem" } }}>
          <Stack
            spacing={2}
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            sx={{ pb: 2 }}
          >
            <Typography variant="h5">
              {I18n.get("Telemonitoring Active Contacts")}
            </Typography>
          </Stack>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {alarmColumns.map((column) => (
                    <TableCell key={column.id}>{column.label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {contactAccNumberState.data
                  .slice(
                    alarmsSameDevPage * rowsPerAlarmsSameDevPage,
                    alarmsSameDevPage * rowsPerAlarmsSameDevPage +
                      rowsPerAlarmsSameDevPage
                  )
                  .map((mappingRow, rowIndex) => (
                    <TableRow key={rowIndex}>
                      {alarmColumns.map((column, colIndex) => (
                        <TableCell key={colIndex} sx={{ padding: "0px" }}>
                          {column.rendercell
                            ? column.rendercell({
                                value: mappingRow[column.id],
                                row: mappingRow,
                              })
                            : mappingRow[column.id] !== undefined &&
                              mappingRow[column.id] !== null
                            ? mappingRow[column.id]
                            : ""}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={contactAccNumberState.data.length}
            page={alarmsSameDevPage}
            onPageChange={(event, newPage) => setAlarmsSameDevPage(newPage)}
            rowsPerPage={rowsPerAlarmsSameDevPage}
            onRowsPerPageChange={(event) => {
              setRowsPerAlarmsSameDevPage(parseInt(event.target.value, 10));
              setAlarmsSameDevPage(0);
            }}
            rowsPerPageOptions={[]}
          />
        </CardContent>
      </Card>
    );
  }
  const theme = useTheme();
  const mobileDevice = !useMediaQuery(theme.breakpoints.up("md"));
  const isMobileDevice = useMediaQuery("(max-width:600px)");
  const [showPopup, setShowPopup] = useState(false);
  const [selectedAuditId, setSelectedAuditId] = useState(null);

  const handleInactivateStore = async (accountNumber, endDate) => {
    try {
      await tlmPostAccountNumberInactive(accountNumber, endDate);
    } catch (error) {
      console.error("Error inactivating store:", error);
    }
  };

  const handleActivateStore = async (accountNumber) => {
    try {
      setApiStatus({
        open: true,
        status: "pending",
        message: I18n.get("Store Reactivation"),
      });
      const restOperation = await tlmPostAccountNumberActive(accountNumber);
      const response = await restOperation.response;
      if (response.statusCode === 200) {
        setApiStatus({
          open: true,
          status: "success",
          message: I18n.get("Store successfully reactivated"),
        });
        setStoreStatus("active"); // Aggiorna lo stato del negozio a "active"
        onCloseNote();
        onClose(); // Chiudi il dialogo dopo aver salvato
      } else {
        setApiStatus({
          open: true,
          status: "error",
          message: I18n.get("Failed to reactivate store"),
        });
      }
      setTimeout(() => {
        setApiStatus((prevState) => ({ ...prevState, open: false }));
      }, 2000);
    } catch (error) {
      setApiStatus({
        open: true,
        status: "error",
        message: I18n.get("Failed to reactivate store"),
      });
      setTimeout(() => {
        setApiStatus((prevState) => ({ ...prevState, open: false }));
      }, 2000);
    }
  };
  function getBrowserName() {
    const userAgent = navigator.userAgent;
    if (userAgent.includes("Firefox")) {
      return "Firefox";
    } else if (userAgent.includes("Edg")) {
      return "Edge";
    } else if (userAgent.includes("Chrome")) {
      return "Chrome";
    }
  }
  const browser = getBrowserName();
  return (
    <>
      <SplitterLayout
        vertical={mobileDevice}
        percentage={true}
        primaryMinSize={30}
        secondaryMinSize={30}
        secondaryInitialSize={50}
      >
        <div>
          <TemplateBox height="15px" />
          <Box
            sx={{
              position: "sticky",
              top: 0,
              display: "flex",
              justifyContent: "flex-end", // Aligns icon to the right
              alignItems: "center",
              zIndex: 10,
              bgcolor: darkMode ? "#1f2020" : "background.paper",
              p: 2,
              gap: 1,
              borderRadius: 2, // Adds rounded corners
              boxShadow: 1, // Adds shadow similar to a card
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontSize: "1.2rem",
                flexGrow: 1, // Ensures the title occupies available space
              }}
            >
              {data.store_name || I18n.get("Store Name")}{" "}
              {/* Fallback if store_name is undefined */}
            </Typography>
            {storeStatus === "active" ? (
              <Button
                onClick={() => setIsInactiveStoreDialogOpen(true)}
                color="primary"
                variant="outlined"
                fullWidth={tableParentIsMD}
                disabled={isGuest}
              >
                {I18n.get("Suspend Store")}
              </Button>
            ) : (
              <Button
                onClick={() => setIsActivateStoreDialogOpen(true)}
                color="primary"
                variant="outlined"
                fullWidth={tableParentIsMD}
                disabled={isGuest}
              >
                {I18n.get("Activate Store")}
              </Button>
            )}

            <Button
              onClick={() => setIsStoreNetworkDialogOpen(true)}
              color="primary"
              variant="outlined"
              fullWidth={tableParentIsMD}
            >
              {I18n.get("Store Network")}
            </Button>
            <IconButton color="secondary" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box
            marginBottom={
              browser === "Firefox"
                ? "83px"
                : browser === "Chrome"
                ? "12px"
                : "0px"
            }
          >
            <TemplateBox mt={1} mb={1}>
              <Grid container spacing={0.5}>
                <Grid item xs={12} md={12} lg={12} mb={0}>
                  <TemplateBox px={2}>
                    <ContactNote
                      data={data}
                      profile={profile}
                      tlmGetAccoutsCountryCode={tlmGetAccoutsCountryCode}
                    />
                  </TemplateBox>
                </Grid>
                <Grid item xs={12} md={12} lg={12} mb={0}>
                  <TemplateBox px={2}>
                    <StoreCard data={data} />
                  </TemplateBox>
                </Grid>

                <Grid item xs={12} md={12} lg={12} mb={0}>
                  <TemplateBox px={2}>{renderAsset()}</TemplateBox>
                </Grid>
                <Grid item xs={12} md={12} lg={12} mb={2}>
                  <TemplateBox px={2}>
                    <RenderPlantNote
                      notePlantState={notePlantState}
                      resourcePrefPage={resourcePrefPage}
                      rowsPerResourcePrefPage={rowsPerResourcePrefPage}
                      setResourcePrefPage={setResourcePrefPage}
                      setRowsPerResourcePrefPage={setRowsPerResourcePrefPage}
                      account_number={data.account_number}
                      endDate={endDate}
                      startDate={startDate}
                      setEndDate={setEndDate}
                      setStartDate={setStartDate}
                      dispatchNotePlant={dispatchNotePlant}
                      profile={profile}
                    />
                  </TemplateBox>
                </Grid>
                <Grid item xs={12} md={12} lg={12} mb={0}>
                  <TemplateBox px={2}>
                    <RenderDeviceNote
                      devicePlantState={devicePlantState}
                      resourcePrefPageDev={resourcePrefPageDev}
                      rowsPerResourcePrefPageDev={rowsPerResourcePrefPageDev}
                      setResourcePrefPageDev={setResourcePrefPageDev}
                      setRowsPerResourcePrefPageDev={
                        setRowsPerResourcePrefPageDev
                      }
                      account_number={data.account_number}
                      endDate={endDateDev}
                      startDate={startDateDev}
                      setEndDate={setEndDateDev}
                      setStartDate={setStartDateDev}
                      dispatchDevicePlant={dispatchDevicePlant}
                      profile={profile}
                      language={language}
                    />
                  </TemplateBox>
                </Grid>
                <Grid item xs={12} md={12} lg={12} mb={0}>
                  <TemplateBox px={2}>
                    <RenderProcessNote
                      deviceProscessState={deviceProscessState}
                      resourcePrefPagePro={resourcePrefPagePro}
                      rowsPerResourcePrefPagePro={rowsPerResourcePrefPagePro}
                      setResourcePrefPagePro={setResourcePrefPagePro}
                      setRowsPerResourcePrefPagePro={
                        setRowsPerResourcePrefPagePro
                      }
                      account_number={data.account_number}
                      endDate={endDateDev}
                      startDate={startDateDev}
                      setEndDate={setEndDateDev}
                      setStartDate={setStartDateDev}
                      dispatchProcessPlant={dispatchProcessPlant}
                      profile={profile}
                    />
                  </TemplateBox>
                </Grid>

                <Grid item xs={12} md={12} lg={12} mb={0}>
                  <TemplateBox px={2}>
                    <ContactsCard data={data} />
                  </TemplateBox>
                </Grid>
                <Grid item xs={12} md={12} lg={12} mb={0}>
                  <TemplateBox px={2}>{renderContactAccInfo()}</TemplateBox>
                </Grid>
                <Grid item xs={12} md={12} lg={12} mb={0}>
                  <TemplateBox px={2}>{renderResourcePref()}</TemplateBox>
                </Grid>
                <Grid item xs={12} md={12} lg={12} mb={0}>
                  <TemplateBox px={2}>
                    <DetailsCard data={data} />
                  </TemplateBox>
                </Grid>

                <Grid item xs={12} md={12} lg={12} mb={2}>
                  <TemplateBox px={2}>{renderAlarmsOnSameDevice()}</TemplateBox>
                </Grid>
              </Grid>
            </TemplateBox>
          </Box>
        </div>
        {showPopup && (
          <Contacts
            isOpen={showPopup}
            onClose={() => setShowPopup(false)}
            rowData={rowData}
          />
        )}
        {isAlarmDialogOpen && (
          <AlarmsOnSameStorePopup
            open={isAlarmDialogOpen}
            onClose={() => setIsAlarmDialogOpen(false)}
            data={rowData}
            language={language}
          />
        )}
      </SplitterLayout>
      <StoreNetworkDialog
        open={isStoreNetworkeDialogOpen}
        accountNumber={data.account_number}
        loading={loading}
        onClose={() => setIsStoreNetworkDialogOpen(false)} // Close the dialog when the button inside is clicked
      />
      <InactivateStoreDialog
        open={isInactiveStoreDialogOpen}
        onClose={() => setIsInactiveStoreDialogOpen(false)}
        accountNumber={data.account_number} // Pass the account number here
        onCloseNote={onCloseNote}
        setStoreStatus={setStoreStatus}
      />
      <ActivateStoreDialog
        open={isActivateStoreDialogOpen}
        onClose={() => setIsActivateStoreDialogOpen(false)}
        accountNumber={data.account_number}
        setStoreStatus={setStoreStatus}
        onCloseNote={onCloseNote}
      />
      <APIStatusDialog
        open={apiStatus.open}
        status={apiStatus.status}
        message={apiStatus.message}
      />
    </>
  );
}

StoreDetails.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  rowData: PropTypes.object.isRequired,
};

export default StoreDetails;
